import { useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { useParams, useNavigate, useMatch } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button,
  Skeleton, Typography,
} from '@mui/material';
import { Add, ArrowBack, ContentCopy, Edit, ExpandMore, Visibility, VisibilityOff } from '@mui/icons-material';
import TooltipIconButton from '../../components/TooltipIconButton';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import { DataGridPremium, GridRenderCellParams, GridRowParams, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid-premium';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { networksService } from '../../services/networks.service';
import { offersService, Offer } from '../../services/offers.service';
import { activityOrderBy } from '../../components/ActivityCell';
import { offersColumns } from '../offers/offers.columns';
import { useModalState } from '../../hooks/useModalState';
import { useServerPagination } from '../../hooks/useServerPagination';
import { useGridHeight } from '../../hooks/useGridHeight';
import { useDebounced } from '../../hooks/useDebounced';
import { buildEntityWhere, resolveSelectedIds, emptySelection } from '../../utils/entity-list';
import EntityGridToolbar from '../../components/EntityGridToolbar';
import { CopyId } from '../../components/CopyId';
import { HistoryButton } from '../../components/EntityHistory';
import NetworkForm from './NetworkForm';
import NetworkPostbackUrl from './NetworkPostbackUrl';

const EDITABLE_FIELDS = ['traffic_cap_limit'];

export default function NetworkDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { setModal } = useModalState('offer');
  const isDuplicate = !!useMatch('/networks/:id/duplicate');
  const isNew = !id;
  const isCreateMode = isNew || isDuplicate;
  const [dirty, setDirty] = useState(false);
  useUnsavedChangesGuard(dirty);

  const { data: network, isLoading } = useQuery({
    queryKey: ['network', id],
    queryFn: () => networksService.getById(id!),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const [childSearch, setChildSearch] = useState('');
  const debouncedChildSearch = useDebounced(childSearch);
  const [statuses, setStatuses] = useState<string[]>(['active']);
  const offersWhere = useMemo(
    () => buildEntityWhere('offer', { search: debouncedChildSearch, statuses, tagIds: [] }, ['name', 'url'],
      [{ offer: { network_id: { op: '=', value: id } } }]),
    [id, debouncedChildSearch, statuses],
  );
  const { gridProps, offset, limit } = useServerPagination(50, offersWhere);
  const [offersSort, setOffersSort] = useState<GridSortModel>([{ field: '_activity', sort: 'desc' }]);
  const offersOrderBy = activityOrderBy(offersSort);
  const gridHeight = useGridHeight();
  const showChildren = !isCreateMode && !!network;
  const { data: offersResult, isFetching: offersLoading } = useQuery({
    queryKey: ['offers', 'by-network', id, offersWhere, offset, limit, offersSort],
    queryFn: () => offersService.query({ where: offersWhere, offset, limit,
      ...(offersOrderBy && { order_by: offersOrderBy, order: offersSort[0]?.sort === 'asc' ? 'asc' as const : 'desc' as const }) }),
    enabled: showChildren,
    placeholderData: (prev) => prev,
  });

  const invalidateOffers = () => queryClient.invalidateQueries({ queryKey: ['offers'] });

  const [selection, setSelection] = useState<GridRowSelectionModel>(emptySelection);
  const selectedIds = useMemo(() => resolveSelectedIds(selection, offersResult?.data || []), [selection, offersResult]);
  const bulkAction = async (fn: () => Promise<unknown>, okMsg: string) => {
    try { await fn(); invalidateOffers(); setSelection(emptySelection); enqueueSnackbar(okMsg, { variant: 'success' }); }
    catch (e) { enqueueSnackbar(e instanceof Error ? e.message : 'Action failed', { variant: 'error' }); }
  };
  const archiveSelected = () => bulkAction(() => Promise.all(selectedIds.map((sid) => offersService.delete(sid))), `Archived ${selectedIds.length}`);
  const unarchiveSelected = () => bulkAction(() => Promise.all(selectedIds.map((sid) => offersService.unarchive(sid))), `Unarchived ${selectedIds.length}`);

  const processRowUpdate = async (newRow: Offer, oldRow: Offer): Promise<Offer> => {
    const patch: Partial<Offer> = {};
    if (newRow.traffic_cap_limit !== oldRow.traffic_cap_limit) {
      const cap = Number(newRow.traffic_cap_limit);
      if (!Number.isFinite(cap) || cap < 0) throw new Error('Traffic cap must be 0 or greater');
      patch.traffic_cap_limit = cap;
    }
    if (Object.keys(patch).length === 0) return oldRow;
    await offersService.update(oldRow.id, patch);
    invalidateOffers();
    enqueueSnackbar('Updated', { variant: 'success' });
    return { ...newRow, ...patch };
  };
  const onProcessRowUpdateError = (e: unknown) => enqueueSnackbar(e instanceof Error ? e.message : 'Update failed', { variant: 'error' });

  const offerColumns = offersColumns({
    includeNetwork: false,
    actions: {
      field: 'actions', headerName: '', width: 90, sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0, alignItems: 'center', height: '100%' }}>
          <TooltipIconButton size="small" title="Edit" onClick={() => setModal({ open: true, id: params.row.id, reason: 'Update', hideParent: true })}>
            <Edit sx={{ fontSize: 16 }} />
          </TooltipIconButton>
          <TooltipIconButton size="small" title="Duplicate" onClick={() => setModal({ open: true, id: params.row.id, reason: 'Duplicate', hideParent: true })}>
            <ContentCopy sx={{ fontSize: 16 }} />
          </TooltipIconButton>
        </Box>
      ),
    },
  });

  if (!!id && isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width={300} height={50} />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (!!id && !isLoading && !network) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/networks')} sx={{ mb: 2 }}>Back</Button>
        <Alert severity="error">Network not found.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={600} sx={{ flexGrow: 1 }}>
          {isNew ? 'New Network' : isDuplicate ? 'Duplicate Network' : network?.name}
        </Typography>
        {!isCreateMode && network && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CopyId id={network.id} />
            <HistoryButton entityType="networks" entityId={network.id} entityName={network.name} />
          </Box>
        )}
      </Box>

      <Accordion defaultExpanded={isCreateMode}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1" fontWeight={600}>Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <NetworkForm
            network={isNew ? undefined : network}
            duplicate={isDuplicate}
            onDirtyChange={setDirty}
            onSaved={(saved) => { if (isCreateMode) { flushSync(() => setDirty(false)); navigate(`/networks/${saved.id}`); } }}
          />
        </AccordionDetails>
      </Accordion>

      {!isCreateMode && network?.postback_url && <NetworkPostbackUrl url={network.postback_url} />}

      {showChildren && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>Offers</Typography>
          </Box>
          <DataGridPremium
            rows={offersResult?.data || []}
            columns={offerColumns}
            loading={offersLoading}
            {...gridProps(offersResult?.total)}
            sortingMode="server"
            sortModel={offersSort}
            onSortModelChange={setOffersSort}
            showToolbar
            slots={{ toolbar: EntityGridToolbar }}
            slotProps={{ toolbar: {
              search: childSearch, onSearch: setChildSearch,
              statuses: { value: statuses, onChange: setStatuses },
              actions: [{ icon: Add, hint: 'Add offer', onClick: () => setModal({ open: true, reason: 'Create', defaults: { network_id: id }, hideParent: true }) }],
              selectedCount: selectedIds.length,
              pluralName: 'offers',
              selectionActions: [
                { icon: VisibilityOff, hint: 'Archive', onClick: archiveSelected, color: 'inherit' as const },
                { icon: Visibility, hint: 'Unarchive', onClick: unarchiveSelected, color: 'inherit' as const },
              ],
            } }}
            checkboxSelection
            disableRowSelectionOnClick
            rowSelectionModel={selection}
            onRowSelectionModelChange={setSelection}
            keepNonExistentRowsSelected
            disableColumnSorting
            disableColumnFilter
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={onProcessRowUpdateError}
            onRowDoubleClick={(params: GridRowParams, event: React.MouseEvent) => {
              const target = event.target as HTMLElement;
              if (target.closest('[data-field="actions"]')) return;
              if (EDITABLE_FIELDS.some((f) => target.closest(`[data-field="${f}"]`))) return;
              setModal({ open: true, id: params.row.id, reason: 'Update', hideParent: true });
            }}
            sx={{ bgcolor: 'background.paper', borderRadius: 2, height: gridHeight }}
          />
        </Box>
      )}
    </Box>
  );
}
